/**
 * UsageCard Component
 * 
 * An inline card showing the user's subscription usage,
 * designed to be embedded in the ActionsMenu dropdown.
 * Inspired by Lovable's credits card design.
 * 
 * Layout:
 * - "Usage" title above the card
 * - Card with Messages count, progress bar, and info list
 * - "Low on messages?" + Upgrade button outside the card
 */

import { useEffect, useState } from 'react';
import back4app2 from '../../back4app2';
import { AgentsSubscriptionInfo } from '@back4app2/sdk';
import LoadingSpinner from '../LoadingSpinner';

interface UsageCardProps {
  /** Whether the card should fetch data (typically when menu opens) */
  isActive?: boolean;
  /** Callback when upgrade button is clicked - opens AgentUpgradeModal */
  onUpgrade?: () => void;
}

const UsageCard = ({ isActive = true, onUpgrade }: UsageCardProps) => {
  const [subscription, setSubscription] = useState<AgentsSubscriptionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch subscription data when card becomes active
  useEffect(() => {
    if (isActive) {
      const fetchSubscription = async () => {
        try {
          setLoading(true);
          setError(null);
          const data = await back4app2.getMyAgentsSubscription();
          setSubscription(data);
        } catch (err) {
          console.error('Error fetching agents subscription:', err);
          setError('Failed to load');
        } finally {
          setLoading(false);
        }
      };
      fetchSubscription();
    }
  }, [isActive]);

  // Calculate usage percentage
  const messagesPercentage = subscription
    ? (subscription.chatMessagesUsage / subscription.chatMessagesHardLimit) * 100
    : 0;

  /**
   * Get color based on usage percentage
   */
  const getBarColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-red-500';
    if (percentage >= 75) return 'bg-yellow-500';
    return 'bg-[#208AEC]';
  };

  /**
   * Format date for display
   */
  const formatResetDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  /**
   * Handle upgrade button click - opens AgentUpgradeModal via callback
   */
  const handleUpgradeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onUpgrade) {
      onUpgrade();
    }
  };

  const isFreePlan = subscription?.planType === 'FREE';

  return (
    <div className="pt-4 pb-2">
      {/* Section Title - outside the card */}
      <p className="text-sm text-white/60 font-medium px-4 mb-2">
        Usage
      </p>

      {/* Usage Card */}
      <div className="mx-3 bg-[#0d1420] border border-white/10 rounded-xl p-4">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <LoadingSpinner width="20px" height="20px" />
          </div>
        ) : error ? (
          <div className="text-center py-4">
            <p className="text-xs text-red-400">{error}</p>
          </div>
        ) : subscription ? (
          <>
            {/* Header: Messages count */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-[#208AEC]">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm font-medium text-white/90">Messages</span>
              </div>
              <span className="text-sm font-semibold text-white">
                <span>{subscription.chatMessagesUsage}</span><span>/</span><span>{subscription.chatMessagesHardLimit}</span>
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-1.5 bg-white/10 rounded-full overflow-hidden mb-3">
              <div 
                className={`h-full ${getBarColor(messagesPercentage)} transition-all duration-300`}
                style={{ width: `${Math.min(messagesPercentage, 100)}%` }}
              />
            </div>

            {/* Info list */}
            <div className="space-y-1.5">
              {isFreePlan ? (
                <>
                  <p className="text-xs text-white/50 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#208AEC] flex-shrink-0" />
                    <span>Resets daily at midnight UTC</span>
                  </p>
                  <p className="text-xs text-white/50 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#208AEC] flex-shrink-0" />
                    <span>No messages will rollover</span>
                  </p>
                </>
              ) : (
                <p className="text-xs text-white/50 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#208AEC] flex-shrink-0" />
                  <span>Resets </span><span>{formatResetDate(subscription.chatMessagesUsageResetAt)}</span>
                </p>
              )}
            </div>

            {/* Pending Payment Warning */}
            {subscription.isPendingPayment && (
              <div className="mt-3 px-2 py-1.5 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                <p className="text-xs text-yellow-400">
                  ⚠️ Payment pending
                </p>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* CTA row - outside the card */}
      {!loading && !error && subscription && (
        <div className="flex items-center justify-between px-4 pt-3">
          <span className="text-xs text-white/50">
            <span>{isFreePlan ? 'Low on messages?' : 'Need more messages?'}</span>
          </span>
          <button
            onClick={handleUpgradeClick}
            className="py-1 px-3 text-xs font-medium bg-[#27AE60] hover:bg-[#19875F] text-white rounded-md transition-colors"
          >
            <span>Upgrade</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default UsageCard;
